import { useCallback, useEffect, useMemo, useState } from 'react'
import { AgentMessage } from '../utils/carliAgent'

export type CarliConversation = {
  id: string
  title: string
  pinned: boolean
  updatedAt: number
  messages: AgentMessage[]
}

export type CarliPreferences = {
  displayName: string
  responseStyle: 'Brief' | 'Balanced' | 'Detailed'
  instructions: string
}

const CONVERSATION_KEY = 'carli.conversations.v1'
const PREFERENCES_KEY = 'carli.preferences.v1'

export const defaultPreferences: CarliPreferences = {
  displayName: '',
  responseStyle: 'Balanced',
  instructions: '',
}

function read<T>(key: string, fallback: T): T {
  if (typeof window === 'undefined') return fallback
  try {
    const raw = window.localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : fallback
  } catch {
    return fallback
  }
}

function write(key: string, value: unknown) {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // Storage may be unavailable; the session still works in memory.
  }
}

// Titles come from the first thing the user asked, the way ChatGPT names threads.
export function titleFromMessage(text: string) {
  const clean = text.replace(/\s+/g, ' ').trim()
  if (clean.length <= 42) return clean
  return `${clean.slice(0, 42).trimEnd()}…`
}

export function useCarliConversations() {
  const [conversations, setConversations] = useState<CarliConversation[]>(() => read<CarliConversation[]>(CONVERSATION_KEY, []))
  const [activeId, setActiveId] = useState<string | null>(null)
  const [preferences, setPreferences] = useState<CarliPreferences>(() => read<CarliPreferences>(PREFERENCES_KEY, defaultPreferences))

  useEffect(() => {
    write(CONVERSATION_KEY, conversations)
  }, [conversations])

  useEffect(() => {
    write(PREFERENCES_KEY, preferences)
  }, [preferences])

  const active = useMemo(
    () => conversations.find((conversation) => conversation.id === activeId) ?? null,
    [conversations, activeId],
  )

  const ordered = useMemo(() => {
    const sorted = [...conversations].sort((a, b) => b.updatedAt - a.updatedAt)
    return {
      pinned: sorted.filter((conversation) => conversation.pinned),
      recent: sorted.filter((conversation) => !conversation.pinned),
    }
  }, [conversations])

  const startNew = useCallback(() => setActiveId(null), [])

  // The id is resolved BEFORE touching state so the updater stays pure and
  // idempotent — otherwise a re-invoked updater spawns duplicate conversations.
  const appendMessage = useCallback(
    (message: AgentMessage): string => {
      const existingId = activeId && conversations.some((conversation) => conversation.id === activeId) ? activeId : null
      const targetId = existingId ?? `c-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`

      setConversations((current) => {
        if (current.some((conversation) => conversation.id === targetId)) {
          return current.map((conversation) =>
            conversation.id === targetId
              ? { ...conversation, messages: [...conversation.messages, message], updatedAt: Date.now() }
              : conversation,
          )
        }
        return [
          {
            id: targetId,
            title: titleFromMessage(message.content),
            pinned: false,
            updatedAt: Date.now(),
            messages: [message],
          },
          ...current,
        ]
      })

      if (!existingId) setActiveId(targetId)
      return targetId
    },
    [activeId, conversations],
  )

  const appendToConversation = useCallback((id: string, message: AgentMessage) => {
    setConversations((current) =>
      current.map((conversation) =>
        conversation.id === id &&
        conversation.messages[conversation.messages.length - 1]?.content !== message.content
          ? { ...conversation, messages: [...conversation.messages, message], updatedAt: Date.now() }
          : conversation,
      ),
    )
  }, [])

  const renameConversation = useCallback((id: string, title: string) => {
    const clean = title.trim()
    if (!clean) return
    setConversations((current) =>
      current.map((conversation) => (conversation.id === id ? { ...conversation, title: clean } : conversation)),
    )
  }, [])

  const togglePinned = useCallback((id: string) => {
    setConversations((current) =>
      current.map((conversation) => (conversation.id === id ? { ...conversation, pinned: !conversation.pinned } : conversation)),
    )
  }, [])

  const deleteConversation = useCallback(
    (id: string) => {
      setConversations((current) => current.filter((conversation) => conversation.id !== id))
      setActiveId((current) => (current === id ? null : current))
    },
    [],
  )

  return {
    conversations,
    ordered,
    active,
    activeId,
    setActiveId,
    startNew,
    appendMessage,
    appendToConversation,
    renameConversation,
    togglePinned,
    deleteConversation,
    preferences,
    setPreferences,
  }
}
